import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import { inventoryApi } from '../../api/inventory-api-instance';
import { humanizeIdentifier } from '../../lib/formatting';
import { siteLabel } from '../../lib/site-label';
import { stockUnitLabels, type StockItem } from '../../domain/fulfillment';

/**
 * Reference data the pick-pack screens show names from. Every lookup falls
 * back to the raw ID, so a missing reference never blanks a row.
 */
export function useFulfillmentReferences() {
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => inventoryApi.listSites() });
  const parts = useQuery({ queryKey: ['parts'], queryFn: () => inventoryApi.listParts() });
  const stockItems = useQuery({
    queryKey: ['stock-items'],
    queryFn: () => fulfillmentApi.listStockItems(),
  });
  const locations = useQuery({
    queryKey: ['warehouse-locations'],
    queryFn: () => fulfillmentApi.listWarehouseLocations(),
  });

  return useMemo(() => {
    const customerById = new Map((customers.data ?? []).map((c) => [c.id, c]));
    const siteById = new Map((sites.data ?? []).map((s) => [s.id, s]));
    const partById = new Map((parts.data ?? []).map((p) => [p.id, p]));
    const stockById = new Map((stockItems.data ?? []).map((s) => [s.id, s]));
    const locationById = new Map((locations.data ?? []).map((l) => [l.id, l]));
    return {
      customers: customers.data ?? [],
      sites: sites.data ?? [],
      parts: parts.data ?? [],
      stockItems: stockItems.data ?? [],
      locations: locations.data ?? [],
      customerName: (id: string) => {
        const c = customerById.get(id);
        return c?.displayName || c?.name || id;
      },
      siteName: (id: string) => siteLabel(siteById.get(id), id),
      partName: (id: string) => {
        const p = partById.get(id);
        return p ? `${humanizeIdentifier(p.type)} · ${p.build} (${p.id})` : id;
      },
      stockName: (id: string) => {
        const s = stockById.get(id);
        return s ? `${s.displayName} (${s.sku})` : id;
      },
      stockItem: (id: string | undefined): StockItem | undefined =>
        id ? stockById.get(id) : undefined,
      unit: (id: string | undefined) => {
        const s = id ? stockById.get(id) : undefined;
        return s ? stockUnitLabels[s.stockUnit] : '';
      },
      locationName: (id: string | undefined) => {
        if (!id) return 'Chosen at release';
        const l = locationById.get(id);
        return l ? l.displayName || l.code : id;
      },
    };
  }, [customers.data, sites.data, parts.data, stockItems.data, locations.data]);
}

export type FulfillmentReferences = ReturnType<typeof useFulfillmentReferences>;
